import React, {
    Children,
    CSSProperties,
    forwardRef,
    memo,
    MouseEventHandler,
    ReactNode,
    useContext,
    useRef
} from 'react'
import styles from './Button.module.sass'
import clsx from 'clsx'
import Skeleton from 'react-loading-skeleton'
import { RequestStatus } from '../../../responses/response'
import { ignore, isDefined } from '../../../utils/utils'
import Tooltip, { TooltipProps } from '../../etc/Tooltip'
import { useRipple } from 'react-use-ripple/dist'
import composeRefs from '@seznam/compose-react-refs'
import { Classes, Colors } from '../../../styles'
import { darken, getLuminance, lighten } from 'polished'
import { inactiveContext, statusContext } from '../../../constants/contexts'

export enum ButtonStyle {
    DEFAULT = 'STYLE_DEFAULT',
    BORDERED = 'STYLE_BORDERED',
    WHITE_BORDERED = 'STYLE_WHITE_BORDERED',
    TINTED = 'STYLE_TINTED',
    TABLE_HEAD = 'STYLE_TABLE_HEAD',
    TABLE_SMALL_HEAD = 'STYLE_TABLE_SMALL_HEAD',
}

export type Justify = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'

export enum Shape {
    NORMAL = 'shape-normal',
    ROUND = 'shape-round'
}

export enum Size {
    SMALL = 'size-small',
    NORMAL = 'size-normal',
    BIG = 'size-big',
    BIG_ICON = 'size-big-icon',
    HUGE = 'size-huge',
    NORMAL_ICON = 'size-normal-icon'
}

export interface Props extends TooltipProps {
    color?: string,
    justify?: Justify,
    buttonStyle?: ButtonStyle,
    buttonShape?: Shape,
    size?: Size,
    onClick?: MouseEventHandler<HTMLDivElement>,
    onDisabledClick?: () => void,
    onMouseDown?: MouseEventHandler<HTMLDivElement>,
    onMouseEnter?: () => void,
    onMouseLeave?: () => void,
    disabled?: boolean,
    active?: boolean,
    inactive?: boolean, // disable interactions
    alwaysVisible?: boolean,
    children: ReactNode | ReactNode[],
}

const Button = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const color = props.color ?? Colors.grey
    const status: RequestStatus = useContext(statusContext)
    const hidden = useContext(inactiveContext)
    const disabled = props.disabled || status === RequestStatus.FETCHING || hidden
    const localRef = useRef<HTMLDivElement>(null)
    useRipple(localRef, {disabled})

    if (hidden && !props.alwaysVisible) {
        return null
    }

    return <div
        className={clsx(props.className, styles.container, Classes.touchTarget)}
        onClick={disabled ? props.onDisabledClick : props.onClick}
        // to fire click before blur
        onMouseDown={event => {
            if (!disabled && event.button === 0) {
                props.onMouseDown?.(event)
            }
        }}
        onMouseEnter={disabled? ignore: props.onMouseEnter}
        onMouseLeave={disabled? ignore: props.onMouseLeave}
    >
        <div
            ref={composeRefs(localRef, ref)}
            className={clsx(
                styles.button,
                styles[props.size ?? Size.NORMAL],
                styles[props.buttonStyle ?? ButtonStyle.DEFAULT],
                styles[props.buttonShape ?? Shape.NORMAL],
                {
                    [styles.active]: props.active,
                    [styles.disabled]: disabled,
                    [styles.inactive]: props.inactive
                }
            )}
            style={{
                '--color': color,
                '--hover': getLuminance(color) < 0.2 ?
                    lighten(0.25, color):
                    darken(0.25, color),
            } as CSSProperties}
        >
            <Tooltip
                title={props.title}
                className={clsx(
                    styles.tooltip,
                    Children.count(props.children) === 1 ? styles.single : styles.multiple,
                    {[styles.text]: typeof props.children === 'string'}
                )}
                styles={{justifyContent: props.justify}}
            >
                {
                    React.Children.toArray(props.children).filter(isDefined).map((child, index) =>
                        <div key={index} className={styles.content}>{child}</div>
                    )
                }
                {
                    (status === RequestStatus.FETCHING) && <Skeleton className={styles.loading}/>
                }
            </Tooltip>
        </div>
    </div>
})

Button.displayName = 'Button'

export default memo(Button)
